// ============================================================
// MongoDBCrudRead.jsx
//
// A branded, interactive guide to querying data in MongoDB.
// Covers find(), findOne(), Query Operators, and Projections.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Tooltip,
} from "@mui/material";

/* MUI Icons */
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import StorageIcon from "@mui/icons-material/Storage";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    borderHover: "rgba(0, 237, 100, 0.6)",
    green: "#00ED64",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.green }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#D1D5DB", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBCrudRead() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="CRUD Operations · Module 06"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Read <span style={{ color: BRAND.green }}>Operations</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        Retrieving data in MongoDB is done through the <code>find()</code> method.
                        Instead of writing strings, you pass objects that act as "filters" to locate exactly the documents you need.
                    </Typography>
                </Box>

                {/* 2. THE BASIC FIND */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={5}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <SearchIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Basic Queries</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            To fetch all documents, call <code>find()</code> with an empty object.
                            To find specific ones, provide key-value pairs that match your document structure.
                        </Typography>
                        <Stack spacing={2}>
                            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}>
                                <Typography variant="caption" sx={{ color: BRAND.green }}>SQL Comparison:</Typography>
                                <Typography variant="body2">SELECT * FROM users WHERE status = "active";</Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <CodeTerminal
                            label="mongosh"
                            code={`// Find all documents\ndb.users.find({});\n\n// Find by equality\ndb.users.find({ status: "active" });\n\n// Find only the first match\ndb.users.findOne({ email: "user@example.com" });`}
                        />
                    </Grid>
                </Grid>



                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 3. QUERY OPERATORS */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <FilterAltIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Comparison Operators</Typography>
                    </Box>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        MongoDB uses special operators starting with <code>$</code> for range queries and complex logic.
                    </Typography>

                    <Grid container spacing={3}>
                        {[
                            { op: "$gt / $lt", desc: "Greater than / Less than", example: "{ price: { $gt: 50 } }" },
                            { op: "$gte / $lte", desc: "Greater/Less than or Equal", example: "{ age: { $gte: 18 } }" },
                            { op: "$in", desc: "Matches any value in an array", example: "{ category: { $in: ['electronics', 'food'] } }" },
                            { op: "$ne", desc: "Not Equal to", example: "{ status: { $ne: 'deleted' } }" }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Paper sx={{ p: 2.5, bgcolor: BRAND.surface, borderRadius: 3, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                    <Typography variant="h6" sx={{ color: BRAND.green, fontWeight: 800 }}>{item.op}</Typography>
                                    <Typography variant="caption" sx={{ color: BRAND.white, display: 'block', mb: 1 }}>{item.desc}</Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, color: BRAND.muted }}>{item.example}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>



                {/* 4. PROJECTIONS */}
                <Grid container spacing={6} alignItems="center" sx={{ mb: 10 }}>
                    <Grid item xs={12} md={7} order={{ xs: 2, md: 1 }}>
                        <CodeTerminal
                            label="mongosh"
                            code={`// Only return 'name' and 'email' (include: 1)\ndb.users.find(\n  { status: "active" },\n  { name: 1, email: 1, _id: 0 }\n);`}
                        />
                    </Grid>
                    <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                            <VisibilityIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                            <Typography variant="h5" sx={{ fontWeight: 800 }}>Projections</Typography>
                        </Box>
                        <Typography sx={{ color: BRAND.muted, mb: 3, lineHeight: 1.8 }}>
                            In SQL, you choose columns with <code>SELECT name, email</code>. In MongoDB, this is called
                            <strong> Projection</strong>. Use the second argument of <code>find()</code> to specify which fields to include (1) or exclude (0).
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 5. LOGICAL OPERATORS */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(0, 237, 100, 0.03)", border: `1px solid ${BRAND.border}` }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                        <PrecisionManufacturingIcon sx={{ color: BRAND.green }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Logical Operators ($and, $or)</Typography>
                    </Box>
                    <Typography variant="body1" sx={{ color: BRAND.muted, mb: 4 }}>
                        When you need to combine multiple conditions, use <code>$or</code> or <code>$and</code>.
                        These take an array of filter objects.
                    </Typography>

                    <CodeTerminal
                        label="Advanced Query"
                        code={`db.products.find({\n  $or: [\n    { price: { $lt: 20 } },\n    { category: "sale" }\n  ]\n});`}
                    />
                </Box>

                {/* 6. PRO-TIP: THE CURSOR */}
                <Box sx={{ mb: 10 }}>
                    <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                        <Stack direction="row" spacing={2}>
                            <Avatar sx={{ bgcolor: BRAND.green }}><StorageIcon /></Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>Understanding the Cursor</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                    <code>db.collection.find()</code> doesn't actually return all documents at once—it returns a <strong>Cursor</strong>.
                                    In the shell, it automatically iterates the first 20. In an app, you can chain methods like
                                    {" "}<code>.limit(5)</code>, <code>.sort({ "{" } age: -1 {"}" })</code>, and <code>.skip(10)</code> to handle pagination efficiently.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <SearchIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Queries Mastered?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>Finding data is the core of any app. Next, we will learn how to modify that data with Update operations.</Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <IconButton sx={{ bgcolor: BRAND.green, color: BRAND.bg, p: 2, borderRadius: 2 }}>
                            <Typography variant="button" sx={{ fontWeight: 900 }}>Explore Update Operators</Typography>
                        </IconButton>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
